import * as React from 'react';
import { Dispatch } from 'react-redux';
import { I18n } from 'react-redux-i18n';

import { StoreState } from '../../../reducers';
import PublicationTemplates from '../../../containers/PublicationCreator/PublicationTemplates';
import ResourceCreationPanel from '../../ResourceCreationPanel';
import Popup from '../../Popup';
import ProgressBar from '../../ResourceSelector/ProgressBar';
import { PopupBodyWrapper } from '../styles';
import {
	hidePublicationCreator,
	createPublicationFromMasterTemplate,
	createProposalFromDriveItem,
	selectTemplate,
	setName,
	removeFromCreationProgress,
} from '../../../containers/PublicationCreator/actions';
import { PublicationSource, SelectedTemplate, PublicationCreationStatus } from '../../../containers/PublicationCreator/reducer';
import { FileError } from '../../../containers/ImportManager/actions';
import { Api } from '../../../typings/api';
import DriveItemUtils from '../../../utils/driveItem';

interface FromTemplateProps {
	isDisplayed: boolean;
	source: PublicationSource | null;
	selectedTemplate: SelectedTemplate | null;
	name: string;
	error: string | null;
	isImportingFile: boolean;
	importingStatus: PublicationCreationStatus;
	importingPercentage: number;
	importingError: FileError | null;
	dispatch: Dispatch<StoreState>;
}

const FromTemplate = ({
	isDisplayed,
	source,
	selectedTemplate,
	name,
	error,
	isImportingFile,
	importingStatus,
	importingPercentage,
	importingError,
	dispatch,
}: FromTemplateProps) => {
	const isFromTemplate = source === PublicationSource.TEMPLATE;
	const [isTemplatesDisplayed, setIsTemplatesDisplayed] = React.useState(isFromTemplate);

	React.useEffect(() => {
		setIsTemplatesDisplayed(isFromTemplate && isDisplayed);
	}, [isDisplayed]);

	const handleTemplateSelect = (data: SelectedTemplate) => {
		dispatch(selectTemplate(data));
		setIsTemplatesDisplayed(false);
	};

	const handlePublicationNameConfirm = (name: string, selectedTemplate: SelectedTemplate) => {
		const { isCustomTemplate, id, format } = selectedTemplate;

		if (isCustomTemplate) {
			dispatch(createProposalFromDriveItem({ id, label: name } as Api.DriveItem, id.toString()));
		} else {
			dispatch(createPublicationFromMasterTemplate(id, name, format));
		}
	};

	const handlePublicationNameCancel = () => {
		if (isFromTemplate) {
			setIsTemplatesDisplayed(true);
		} else {
			dispatch(hidePublicationCreator());
		}
	};

	const getProgressBarLabel = () => {
		if (importingStatus) {
			return `messages.${importingStatus.toLowerCase()}`;
		}

		return selectedTemplate && DriveItemUtils.isProposal(selectedTemplate.format)
			? 'messages.create_proposal'
			: 'messages.create_publication';
	};

	const getResourceCreationPanelLabel = () => (
		selectedTemplate && DriveItemUtils.isProposal(selectedTemplate.format)
			? 'messages.proposal_name'
			: 'messages.publication_name'
	);

	const handlePublicationCreationCancel = () => {
		dispatch(removeFromCreationProgress());
		setIsTemplatesDisplayed(true);
	};

	return (
		<>
			{/* in case creation proposal from template <PublicationTemplates />
				has to be always visible to avoid blicking when <ResourceCreationPanel /> is appearing
			*/}
			{(isFromTemplate ? isDisplayed : isTemplatesDisplayed) &&
				<PublicationTemplates
					onSelect={handleTemplateSelect}
					onClose={() => dispatch(hidePublicationCreator())}
				/>
			}
			<Popup
				isDisplayed={isImportingFile && isDisplayed}
				label={I18n.t(getProgressBarLabel())}
				onCancel={handlePublicationCreationCancel}
			>
				<PopupBodyWrapper>
					<ProgressBar
						percentage={importingPercentage}
						error={importingError}
						hasCancelButton={true}
						onCancel={handlePublicationCreationCancel}
					/>
				</PopupBodyWrapper>
			</Popup>
			<ResourceCreationPanel
				label={getResourceCreationPanelLabel()}
				isOpen={!isImportingFile && isDisplayed && !isTemplatesDisplayed}
				autoFocus={false}
				icon="iconset_uprez_proposal-blue"
				fields={{ name: { value: name } }}
				errors={{ name: error }}
				onFieldChange={(name, value) => dispatch(setName(value))}
				onConfirm={({ name }) => handlePublicationNameConfirm(name.value, selectedTemplate as SelectedTemplate)}
				onCancel={handlePublicationNameCancel}
			/>
		</>
	);
};

export default FromTemplate;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/PublicationCreator/FromTemplate/index.tsx